<script lang="ts">
  import Icon from "./Icon.svelte";
  // Wiederverwendbares Karten-Raster: SPALTEN-STAPEL (aus home-dash
  // portiert, Konzept V2). Eine Page gibt Gruppe + Karten-IDs + ein
  // card-Snippet — CardGrid übernimmt Sortieren, Breite-Toggle und
  // Persistenz (prefs, Server-SSOT + WS-Sync).
  //
  // BREIT (Raster ab 50rem breit): 4 Spalten-Stapel. Gemessen wird das
  // Raster selbst, nicht das Fenster -- in der geteilten Ansicht der Shell
  // ist eine Spalte schmal, auch wenn das Fenster breit ist (bis 03.10.
  // standen dort vier gequetschte Karten nebeneinander). Karten sind mindestens so
  // hoch wie ihr Inhalt (periodische Naturhöhen-Probe, auf Rasterzellen
  // gerundet). Langes Drücken öffnet den Anordnen-Modus: Ziehen wählt
  // Spalte + Einfügeposition (Live-Vorschau, Einrast-Raster sichtbar),
  // das Kürzel oben rechts schaltet die Breite (1 ↔ 2 Spalten, nur wo
  // allowWide(id) das erlaubt), der Griff an der Unterkante zieht die
  // Höhe (rastet am Raster ein, nie kleiner als der Inhalt).
  //
  // MOBIL/TABLET: gestapelt bzw. 2-spaltig in gemerkter REIHENFOLGE
  // (card-order.ts, Long-Press). Im Verschiebemodus kollabieren die
  // Karten auf ihre Kopfzeile — Überblick statt Scrollen.
  import type { Snippet } from "svelte";
  import {
    GRID_COLS,
    resolveLists,
    saveLists,
    insertAt,
    toggleWide,
    entferneKarte,
    zeigeKarte,
    setRows,
    packLayout,
    type CardLists,
  } from "./card-layout";
  import { orderItems, moveCard } from "./card-order";
  import { pressReorder, type PressReorderParams } from "./press-reorder";
  import { t } from "./i18n.svelte";
  import { seiteVorn } from "./shell/page-route";

  interface Props {
    /** Prefs-Gruppe (Key `card_layout_<group>` bzw. `card_order_<group>`). */
    group: string;
    /** Aktuelle Karten-IDs (reaktiv; neue IDs landen in der kürzesten Spalte). */
    ids: string[];
    /** Rendert den Karteninhalt. Zweites Argument: Anordnen-Modus aktiv. */
    card: Snippet<[string, boolean]>;
    /** Sinnvoller Rahmen: darf diese Karte 2 Spalten breit werden? Default: ja. */
    allowWide?: (id: string) => boolean;
    /** Karten, die ohne gespeicherten Stand 2-spaltig starten. */
    defaultWide?: string[];
    /** Zahnrad an der Karte im Anordnen-Modus: was soll sie zeigen?
     *  Nur gesetzt, wo die Seite dafuer etwas anzubieten hat. */
    onConfigure?: (id: string) => void;
    /** Beschriftung einer Karte fuer das "Karte hinzufuegen"-Menue.
     *  Ohne sie steht dort die ID. */
    labelOf?: (id: string) => string;
    /** Grid-Klassen der gestapelten Ansicht (schmales Raster). Breiten
     *  als Container-Abfrage (@lg), nicht als Fenster-Breakpoint. */
    mobileClass?: string;
    /** Kartenhöhen angleichen: alle Karten der Gruppe bekommen die Höhe
     *  der höchsten (wie die alten Bento-Zeilen). Karten strecken sich
     *  visuell auf die Slot-Höhe; gemessen wird die Naturhöhe per
     *  periodischem Probe (siehe unten). */
    equalRows?: boolean;
    /** Anordnen-Modus (bindbar): Pages können eine eigene Modus-Leiste
     *  (Reset/Fertig) zeigen und den Modus programmatisch beenden. */
    arranging?: boolean;
  }
  let {
    group,
    ids,
    card,
    allowWide = () => true,
    labelOf = (id: string) => id,
    onConfigure,
    defaultWide = [],
    mobileClass = "grid-cols-1 @lg:grid-cols-2",
    equalRows = false,
    arranging = $bindable(false),
  }: Props = $props();

  // ── Breakpoint: Spalten-Layout, sobald das RASTER breit genug ist ──
  // 50rem = vier Karten zu je gut 11rem plus Abstände. Die Breite des
  // Rahmens hängt nicht vom Inhalt ab (Block über die volle Zone), der
  // Wechsel kann sich also nicht selbst auslösen.
  const BREIT_AB_REM = 50;
  let rahmenEl = $state<HTMLElement | undefined>();
  let isDesktop = $state(false);
  $effect(() => {
    const el = rahmenEl;
    if (!el) return;
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const upd = () => (isDesktop = el.clientWidth >= BREIT_AB_REM * rem);
    upd();
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    return () => ro.disconnect();
  });

  // ══ Desktop: Spalten-Stapel ══════════════════════════════════════════
  const savedLists = $derived(resolveLists(group, ids, defaultWide));
  let draft = $state<CardLists | null>(null); // Live-Vorschau während des Ziehens
  const lists = $derived(draft ?? savedLists);

  // Karten-Inhaltshöhen (px), live per ResizeObserver — daraus die
  // Zeilen-Spannweite. Vor der ersten Messung greift ein Schätzwert.
  const px = $state<Record<string, number>>({});
  const ROW_REM = 2.125; // grid-auto-rows (fein); Gap = 1rem
  let unitPx = $state(36); // Zelle in px (aus dem echten Grid gelesen)
  let gapPx = $state(16);
  // Naturhoehe (Inhalt) in Rasterzeilen.
  function naturalRows(id: string): number {
    const h = px[id];
    if (!h) return 4;
    return Math.max(1, Math.ceil((h + gapPx) / (unitPx + gapPx)));
  }
  // equalRows: alle Karten bekommen die Zeilen der höchsten — Wellen
  // laufen im Gleichschritt, die Zone wirkt wie ein festes Raster.
  const maxNaturalRows = $derived.by(() => {
    let m = 1;
    for (const id of ids) m = Math.max(m, naturalRows(id));
    return m;
  });
  // Manuelle Hoehe waehrend des Ziehens am unteren Griff (Live-Vorschau).
  let resizeDraft = $state<{ id: string; rows: number } | null>(null);
  // Sinnvoller Rahmen fuer manuelle Hoehe: nie kleiner als der Inhalt
  // (nichts wird abgeschnitten), Deckel gegen Endlos-Karten.
  const MAX_OVERRIDE_ROWS = 40;
  function rowsOfCard(id: string): number {
    const nat = naturalRows(id);
    let want: number | undefined;
    if (resizeDraft && resizeDraft.id === id) want = resizeDraft.rows;
    else if (lists.rows[id]) want = lists.rows[id];
    else if (equalRows) want = maxNaturalRows;
    if (want === undefined) return nat;
    return Math.max(nat, Math.min(MAX_OVERRIDE_ROWS, want));
  }
  const placed = $derived(packLayout(lists, rowsOfCard));

  let gridEl = $state<HTMLElement | undefined>();
  $effect(() => {
    if (!gridEl) return;
    const cs = getComputedStyle(gridEl);
    unitPx = parseFloat(cs.gridAutoRows) || 36;
    gapPx = parseFloat(cs.rowGap) || 16;
  });

  // Naturhöhen-Probe: Karten sind per CSS auf ihre Slot-Höhe gestreckt
  // (bündige Kanten am Raster). Für die Messung wird die Streckung kurz
  // aufgehoben (Klasse "probing"), alle Naturhöhen in einem Rutsch
  // gelesen, Streckung wieder an. Ein gebündelter Reflow pro Sekunde —
  // billig bei einer Handvoll Karten, und fängt Inhalts-Wachstum UND
  // -Schrumpfen zuverlässig (ein ResizeObserver auf dem gestreckten
  // Element wäre zirkulär und sieht Schrumpfen nie).
  // Nur vorn: ein gehaltener, verdeckter Tab hat kein Layout -- die Probe
  // erzwang es jede Sekunde für nichts (Repo-Prüfung 21.10.2026, zweite
  // Runde). Wieder vorn misst sie sofort.
  const vorn = seiteVorn();
  $effect(() => {
    if (!isDesktop || !vorn()) return;
    const el = gridEl;
    if (!el) return;
    const probe = () => {
      el.classList.add("probing");
      for (const slot of el.querySelectorAll<HTMLElement>("[data-slot]")) {
        const c = slot.firstElementChild as HTMLElement | null;
        const id = slot.dataset.slot;
        if (!c || !id) continue;
        const h = c.offsetHeight;
        if (h > 0 && px[id] !== h) px[id] = h;
      }
      el.classList.remove("probing");
    };
    probe();
    const timer = setInterval(probe, 1_000);
    return () => clearInterval(timer);
  });

  // ── Höhe ziehen (unterer Griff, nur im Anordnen-Modus) ──────────────
  function startResize(e: PointerEvent, id: string) {
    e.preventDefault();
    e.stopPropagation();
    const slot = gridEl?.querySelector<HTMLElement>(`[data-slot="${id}"]`);
    if (!slot) return;
    const top = slot.getBoundingClientRect().top;
    document.body.style.userSelect = "none";
    const onMove = (ev: PointerEvent) => {
      const rows = Math.round((ev.clientY - top + gapPx / 2) / (unitPx + gapPx));
      resizeDraft = { id, rows: Math.max(1, rows) };
    };
    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
      document.body.style.userSelect = "";
      if (resizeDraft && resizeDraft.id === id) {
        const nat = naturalRows(id);
        const rows = Math.max(nat, Math.min(MAX_OVERRIDE_ROWS, resizeDraft.rows));
        // Auf Inhaltshoehe zurueckgezogen -> Override loeschen statt
        // eine Zahl zu speichern, die eh der Naturhoehe entspricht.
        saveLists(group, setRows(lists, id, rows <= nat ? null : rows));
      }
      resizeDraft = null;
    };
    onMove(e);
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onUp);
  }

  let dragId = $state<string | null>(null);

  // Anordnen-Modus verlassen: Tap außerhalb einer Karte (und außerhalb
  // einer per data-arrange-keep markierten Modus-Leiste) oder Escape.
  $effect(() => {
    if (!arranging) return;
    const onDown = (e: PointerEvent) => {
      const el = e.target as HTMLElement | null;
      if (
        el?.closest(
          `[data-slot-group="${group}"],[data-reorder-group="${group}"],[data-arrange-keep]`,
        )
      )
        return;
      arranging = false;
    };
    const onKey = (e: KeyboardEvent) => {
      // Steht ein Dialog offen (Karte einrichten), gehoert Escape IHM:
      // sonst raeumt ein Schliessen gleich den ganzen Anordnen-Modus mit
      // ab, und die Zahnraeder sind weg.
      if (e.key !== "Escape" || document.querySelector("[role=dialog]")) return;
      arranging = false;
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
    };
  });

  // Zeiger → (Spalte, Einfügeindex): Index = wie viele Karten der
  // Ziel-Spalte mit ihrer Mitte ÜBER dem Zeiger liegen.
  function dropTarget(x: number, y: number, id: string): { col: number; index: number } | null {
    if (!gridEl) return null;
    const rect = gridEl.getBoundingClientRect();
    const colW = (rect.width - gapPx * (GRID_COLS - 1)) / GRID_COLS;
    const col = Math.max(0, Math.min(GRID_COLS - 1, Math.floor((x - rect.left) / (colW + gapPx))));
    const rowF = (y - rect.top) / (unitPx + gapPx);
    let index = 0;
    for (const other of lists.cols[col]) {
      if (other === id) continue;
      const p = placed[other];
      if (p && p.r + p.rows / 2 < rowF) index++;
    }
    return { col, index };
  }

  // Long-Press/Drag je Karte (nur Desktop). Innere Gesten-Scopes
  // (Overlays, Breite-Kürzel, verschachtelte Reorder-Zeilen) werden
  // übersprungen.
  function slotDown(e: PointerEvent, id: string) {
    if (!isDesktop || e.button > 0) return;
    const el = e.target as HTMLElement;
    if (el.closest("[data-no-reorder]") || el.closest("[data-size-handle]")) return;
    const inner = el.closest("[data-reorder-id]");
    if (inner) return;

    const sx = e.clientX;
    const sy = e.clientY;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const cleanup = () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
    };
    const onMove = (ev: PointerEvent) => {
      const d = Math.hypot(ev.clientX - sx, ev.clientY - sy);
      if (!arranging && d > 8) cleanup(); // Scroll/Klick — kein Long-Press
      if (arranging && d > 4) {
        cleanup();
        beginDrag(id);
      }
    };
    const onUp = () => cleanup();
    if (!arranging) {
      timer = setTimeout(() => {
        cleanup();
        arranging = true;
        beginDrag(id);
      }, 450);
    }
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onUp);
  }

  function beginDrag(id: string) {
    dragId = id;
    document.body.style.userSelect = "none";
    window.getSelection()?.removeAllRanges();
    const onMove = (ev: PointerEvent) => {
      const target = dropTarget(ev.clientX, ev.clientY, id);
      if (!target) return;
      draft = insertAt(savedLists, id, target.col, target.index);
    };
    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
      document.body.style.userSelect = "";
      if (draft) saveLists(group, draft);
      draft = null;
      dragId = null;
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onUp);
  }

  function onToggleWide(id: string) {
    saveLists(group, toggleWide(lists, id));
  }

  // Entfernen heisst ausblenden, nicht loeschen: die Karte wandert in
  // das "Karte hinzufuegen"-Menue der Leiste unter dem Raster und kommt
  // mit einem Klick zurueck.
  function onEntferne(id: string) {
    saveLists(group, entferneKarte(lists, id));
  }
  function onHinzufuegen(id: string) {
    saveLists(group, zeigeKarte(lists, id));
  }
  // Nur platzierte Karten rendern -- entfernte haben keinen Slot und
  // wuerden sonst irgendwo ins Raster rutschen.
  const sichtbareIds = $derived(lists.cols.flat());

  function slotStyle(id: string): string {
    const p = placed[id];
    if (!p) return "";
    return `grid-column:${p.c + 1} / span ${p.w};grid-row:${p.r + 1} / span ${p.rows};`;
  }

  // ══ Mobil/Tablet: Reihenfolge + Header-Kollaps im Modus ══════════════
  const orderedMobileIds = $derived(
    orderItems(group, sichtbareIds.map((id) => ({ id })), (c) => c.id).map((c) => c.id),
  );
  function rp(id: string): PressReorderParams {
    return {
      group,
      id,
      armed: arranging,
      onActivate: () => (arranging = true),
      onReorder: (g, from, to) => moveCard(g, orderedMobileIds, from, to),
      liftClass: "z-30 shadow-2xl ring-2 ring-accent/70 opacity-95",
    };
  }
</script>

<!-- Rahmen: an ihm wird die Breite gemessen, und er ist der Container
     für die Spaltenwahl der gestapelten Ansicht (@lg). -->
<div bind:this={rahmenEl} class="@container">
{#if isDesktop}
  <!-- Breit: 4 Spalten-Stapel; feine Zeilen nur als Snap-Rhythmus. -->
  <div
    bind:this={gridEl}
    class="grid-slots relative grid gap-4"
    class:arranging
    style="grid-template-columns: repeat({GRID_COLS}, minmax(0, 1fr)); grid-auto-rows: {ROW_REM}rem;"
  >
    {#if arranging}
      <!-- Einrast-Raster: Spalten-Schächte + Zeilen-Rhythmus, nur im
           Anordnen-Modus sichtbar. Liegt UNTER den Karten (z-0). -->
      <div
        class="pointer-events-none absolute inset-0 z-0 grid gap-4"
        style="grid-template-columns: repeat({GRID_COLS}, minmax(0, 1fr));"
        aria-hidden="true"
      >
        {#each Array.from({ length: GRID_COLS }) as _, i (i)}
          <div class="guide-col rounded-xl"></div>
        {/each}
      </div>
    {/if}
    {#each sichtbareIds as id (id)}
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <div
        data-slot={id}
        data-slot-group={group}
        onpointerdown={(e) => slotDown(e, id)}
        class="slot relative z-10 min-h-0 rounded-xl transition-shadow
          {dragId === id ? 'z-30 opacity-95 shadow-2xl ring-2 ring-accent/70' : ''}
          {arranging && dragId !== id ? 'ring-1 ring-accent/25' : ''}"
        style={slotStyle(id)}
      >
        {@render card(id, arranging)}
        {#if arranging}
          <button
            type="button"
            data-size-handle
            onclick={() => onEntferne(id)}
            title={t("cardgrid.remove", "Karte entfernen — sie kommt über die Leiste unten zurück")}
            aria-label={t("cardgrid.remove", "Karte entfernen — sie kommt über die Leiste unten zurück")}
            class="border-border bg-bg-elev text-fg-muted hover:text-danger absolute top-2 left-2 z-20 flex h-7 w-7 items-center justify-center rounded-lg border shadow"
          >
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        {/if}
        {#if arranging && onConfigure}
          <button
            type="button"
            data-size-handle
            onclick={() => onConfigure(id)}
            title={t("cardgrid.configure", "Was die Karte zeigt")}
            aria-label={t("cardgrid.configure", "Was die Karte zeigt")}
            class="border-border bg-bg-elev text-fg-muted hover:text-accent absolute top-2 left-11 z-20 flex h-7 w-7 items-center justify-center rounded-lg border shadow"
          >
            <Icon name="settings" size={13} />
          </button>
        {/if}
        {#if arranging && allowWide(id)}
          <button
            type="button"
            data-size-handle
            onclick={() => onToggleWide(id)}
            title={lists.wide[id]
              ? t("cardgrid.narrow", "Auf 1 Spalte verschmälern")
              : t("cardgrid.widen", "Auf 2 Spalten verbreitern")}
            class="border-accent/50 bg-bg-elev text-accent absolute top-2 right-2 z-20 flex h-7 items-center justify-center rounded-lg border px-2 t-meta font-medium shadow"
          >
            {lists.wide[id] ? "⇥ 1" : "⇤ 2"}
          </button>
        {/if}
        {#if arranging}
          <!-- Höhe ziehen: Griff an der Unterkante, rastet am Zeilen-
               Raster ein; Minimum ist immer die Inhaltshöhe. -->
          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <div
            data-size-handle
            onpointerdown={(e) => startResize(e, id)}
            title={t("cardgrid.height", "Höhe ziehen — rastet am Raster ein, nie kleiner als der Inhalt")}
            class="resize-handle absolute inset-x-0 -bottom-2 z-20 flex h-4 cursor-ns-resize items-center justify-center"
          >
            <span class="bg-accent/70 h-1 w-10 rounded-full shadow"></span>
          </div>
        {/if}
      </div>
    {/each}
  </div>
  {@render hinzufuegenLeiste()}
{:else}
  <!-- Mobil/Tablet: gemerkte Reihenfolge; im Modus nur Kopfzeilen. -->
  <div
    class="grid items-start gap-4 {mobileClass}"
    class:reorder-armed={arranging}
    class:collapse-cards={arranging}
  >
    {#each orderedMobileIds as id (id)}
      <div use:pressReorder={rp(id)} class="slot-m rounded-xl">
        {@render card(id, arranging)}
      </div>
    {/each}
  </div>
  {@render hinzufuegenLeiste()}
{/if}
</div>

{#snippet hinzufuegenLeiste()}
  {#if arranging && lists.hidden.length > 0}
    <div class="border-border/60 mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-dashed px-3 py-2">
      <span class="text-fg-subtle t-meta">{t("cardgrid.add", "Karte hinzufügen")}</span>
      {#each lists.hidden as id (id)}
        <button
          type="button"
          data-no-reorder
          onclick={() => onHinzufuegen(id)}
          class="border-border text-fg-muted hover:text-accent hover:border-accent/50 rounded-lg border ctrl-s t-meta font-medium transition"
        >
          + {labelOf(id)}
        </button>
      {/each}
    </div>
  {/if}
{/snippet}

<style>
  /* Karten sind Bedienflaechen: Long-Press/Ziehen darf keinen Text
     markieren (Desktop-Selektion, iOS-Callout) -- dauerhaft aus. */
  .slot,
  .slot-m {
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
  }
  /* Karten fuellen ihren (auf Rasterzeilen gerundeten) Slot -- Kanten
     enden buendig am Raster, manuelle Hoehen-Overrides werden sichtbar.
     Waehrend des Probens (Naturhoehe messen) kurz zurueck auf auto;
     .probing kommt zur Laufzeit via classList (synchrone Messung) --
     :global, damit Svelte den Selektor nicht wegprunt. */
  .grid-slots .slot > :global(*:first-child) {
    height: 100% !important;
  }
  .grid-slots:global(.probing) .slot > :global(*:first-child) {
    height: auto !important;
  }
  /* Einrast-Raster im Anordnen-Modus: pro Spalte ein Schacht mit
     gestrichelter Kontur + horizontale Linien im Zeilen-Rhythmus
     (grid-auto-rows 2.125rem + 1rem Gap). */
  .guide-col {
    border: 1.5px dashed color-mix(in oklch, var(--marke) 35%, transparent);
    background-color: color-mix(in oklch, var(--marke) 4%, transparent);
    background-image: repeating-linear-gradient(
      to bottom,
      transparent 0,
      transparent calc(3.125rem - 1px),
      color-mix(in oklch, var(--marke) 20%, transparent) calc(3.125rem - 1px),
      color-mix(in oklch, var(--marke) 20%, transparent) 3.125rem
    );
  }
  /* Anordnen-Modus (Desktop): Karteninhalt einfrieren, Gesten gehoeren
     dem Stapel; Slots zeigen den Greif-Cursor. */
  .grid-slots.arranging .slot {
    cursor: grab;
    touch-action: none;
  }
  .grid-slots.arranging .slot > :global(*:first-child) {
    pointer-events: none;
  }
  /* Verschiebemodus (Mobil): Karten auf die Kopfzeile reduzieren --
     Ueberblick beim Sortieren. Wurzel jeder Karte behaelt ihr Padding,
     alles nach dem Header (erstes Kind) verschwindet. */
  .collapse-cards .slot-m > :global(*:first-child) > :global(*:not(:first-child)) {
    display: none;
  }
  .reorder-armed > :global(div) {
    cursor: grab;
    outline: 1.5px solid color-mix(in oklch, var(--marke) 45%, transparent);
    outline-offset: 3px;
    border-radius: 0.75rem;
  }
</style>
